import React, { useState } from "react";
import { X, Image as ImageIcon, ZoomIn } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

/** Convierte URLs relativas /api/files/... a absolutas. */
function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

/**
 * Fase 14.5 · Grid de attachments (imágenes) con lightbox modal.
 * Props:
 *   - attachments: [{id, type, url, filename, width_px, height_px, alt_text, size_bytes}]
 *   - editable: boolean — muestra botón X para remover
 *   - onRemove: (id) => void
 */
export default function AttachmentGrid({ attachments = [], editable = false,
                                          onRemove }) {
  const [lightbox, setLightbox] = useState(null); // attachment object
  const images = (attachments || []).filter((a) => a.type === "image");
  if (images.length === 0) return null;

  const cols = images.length === 1 ? "grid-cols-1" :
                 images.length === 2 ? "grid-cols-2" :
                 "grid-cols-2 md:grid-cols-3";

  return (
    <>
      <div
        data-testid="forum-attachments-grid"
        className={`grid ${cols} gap-2 mt-3`}
      >
        {images.map((a) => (
          <div key={a.id} className="relative group">
            <button
              type="button"
              onClick={() => setLightbox(a)}
              data-testid="forum-attachment-image"
              className="block w-full overflow-hidden rounded-md border border-gray-800 bg-black/30 hover:border-brand-orange-500/50 transition-colors"
              title={a.alt_text || a.filename}
            >
              <img
                src={resolveUrl(a.url)}
                alt={a.alt_text || a.filename || "attachment"}
                className="w-full h-40 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
                <ZoomIn className="w-6 h-6 text-white" />
              </div>
            </button>
            {editable && (
              <button
                type="button"
                onClick={() => onRemove?.(a.id)}
                data-testid={`forum-attachment-remove-${a.id}`}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/70 hover:bg-red-500/90 text-white border border-gray-700"
                title="Quitar"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="forum-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            data-testid="forum-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={resolveUrl(lightbox.url)}
            alt={lightbox.alt_text || lightbox.filename}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-md"
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="absolute bottom-4 left-4 right-4 text-center text-xs text-gray-400 font-mono"
            onClick={(e) => e.stopPropagation()}
          >
            <ImageIcon className="w-3 h-3 inline mr-1" />
            {lightbox.filename} · {lightbox.width_px}x{lightbox.height_px}px · {Math.round((lightbox.size_bytes || 0) / 1024)} KB
          </div>
        </div>
      )}
    </>
  );
}
